// Copyright (c) Ping. All rights reserved.
//
// Builds + dispatches `com.ping.video` through MLS. The video bytes are
// encrypted + uploaded as an S3 blob (like images/files); a first-frame
// poster (probed client-side) is encrypted + uploaded as a second blob
// referenced by `poster_ref`. The receiver decrypts the poster eagerly
// (small) and the video lazily on first play.

import {
  commitSentMessage as sharedCommitSentMessage,
  encodeEnvelopeJsonBytes,
  encodeEnvelopeJsonString,
  lastMessagePreview,
  markMessageFailed as sharedMarkMessageFailed,
  mintEventId,
  persistSentMessage as sharedPersistSentMessage,
} from '@ping/ui';

import { log } from '@core/log/log';
import { retrieveIdentity } from '@core/persistence/identity';

import { blobRefToWire, type BlobRefWire,encryptMediaBlob } from './mediaCrypto';
import { uploadCiphertextMultipart } from './mediaUpload';
import { webConversationsPersister, webMessagesPersister } from './persistenceAdapters';
import { type ProbedVideo,probeVideoFile } from './probeVideoFile';

interface SentMessageEnvelope {
  conversation_id: Uint8Array;
  epoch: number | bigint;
  content_hash: Uint8Array;
  hlc: { wall_ms: number | bigint; logical: number };
}

interface SendableConversation {
  send(plaintext: Uint8Array): Promise<SentMessageEnvelope>;
}

export interface VideoEnvelopePayload {
  readonly blob_ref: BlobRefWire;
  readonly poster_ref?: BlobRefWire | undefined;
  readonly w: number;
  readonly h: number;
  readonly dur_ms: number;
  readonly mime: string;
  readonly caption?: string | undefined;
}

export interface VideoEnvelope {
  readonly kind: 'com.ping.video';
  readonly payload: VideoEnvelopePayload;
  readonly parent_id?: string;
}

export interface SendVideoMessageOptions {
  readonly conv: SendableConversation;
  readonly conversationId: string;
  readonly file: File;
  /** Pre-probed metadata (from the staged tray). Re-probed if omitted. */
  readonly probed?: ProbedVideo | undefined;
  readonly signal?: AbortSignal | undefined;
  readonly localId?: string | undefined;
  readonly caption?: string | undefined;
  readonly onProgress?: ((fraction: number) => void) | undefined;
  readonly parentId?: string | undefined;
}

export interface SendVideoMessageResult {
  readonly contentHashHex: string;
  readonly messageId: string;
  /** Local object URL of the source video (instant sender-side playback). */
  readonly videoObjectUrl: string;
  readonly posterObjectUrl?: string | undefined;
}

export async function sendVideoMessage(
  opts: SendVideoMessageOptions,
): Promise<SendVideoMessageResult> {
  const { conv, conversationId, file, signal, localId, caption, onProgress, parentId } = opts;

  const identity = await retrieveIdentity();
  const probed = opts.probed ?? (await probeVideoFile(file));
  const mime = file.type !== '' ? file.type : 'video/mp4';

  // 1) Encrypt + upload the poster first (small, fast) so `poster_ref` is
  //    ready; failures degrade to a poster-less tile.
  let posterRef: BlobRefWire | undefined;
  if (probed.posterBytes !== undefined && probed.posterBytes.byteLength > 0) {
    try {
      const enc = await encryptMediaBlob(probed.posterBytes);
      const { mediaId } = await uploadCiphertextMultipart(enc.ciphertext, probed.posterMime, signal);
      posterRef = blobRefToWire(mediaId, enc.secrets, enc.hash);
    } catch (err) {
      log.warn({ event: 'video.poster.upload_failed', err: (err as Error).message });
    }
  }

  // 2) Encrypt + upload the video bytes (progress drives the tile %).
  const plaintext = new Uint8Array(await file.arrayBuffer());
  const { ciphertext, secrets, hash } = await encryptMediaBlob(plaintext);
  let mediaId: string;
  try {
    ({ mediaId } = await uploadCiphertextMultipart(ciphertext, mime, signal, onProgress));
  } catch (err) {
    if (localId !== undefined) {
      await sharedMarkMessageFailed({ messages: webMessagesPersister, id: localId });
    }
    throw err;
  }

  const blobRef = blobRefToWire(mediaId, secrets, hash);
  const payload: VideoEnvelopePayload = {
    blob_ref: blobRef,
    ...(posterRef !== undefined ? { poster_ref: posterRef } : {}),
    w: probed.width,
    h: probed.height,
    dur_ms: probed.durationMs,
    mime,
    ...(caption !== undefined && caption.length > 0 ? { caption } : {}),
  };
  const eventId = localId ?? mintEventId();
  const encodable = {
    kind: 'com.ping.video' as const,
    event_id: eventId,
    // Thread reply → self-identify with `payload.thread: true` so the main
    // timeline folds it out (parent_id alone also marks an inline reply-quote).
    // For non-text kinds parent_id only ever means a thread reply.
    payload: { ...payload, ...(parentId !== undefined ? { thread: true } : {}) },
    ...(parentId !== undefined ? { parent_id: parentId } : {}),
  };
  const payloadBody = encodeEnvelopeJsonString(encodable);
  const payloadBytes = encodeEnvelopeJsonBytes(encodable);

  let sentEnvelope: SentMessageEnvelope;
  try {
    sentEnvelope = await conv.send(payloadBytes);
  } catch (err) {
    if (localId !== undefined) {
      await sharedMarkMessageFailed({ messages: webMessagesPersister, id: localId });
    }
    throw err;
  }
  const contentHashHex = bytesToHex(sentEnvelope.content_hash);

  const sentAt = Date.now();
  const epoch =
    typeof sentEnvelope.epoch === 'bigint' ? Number(sentEnvelope.epoch) : sentEnvelope.epoch;

  if (localId !== undefined) {
    await webMessagesPersister.update(localId, { body: payloadBody });
    if (identity) {
      const preview = lastMessagePreview(payloadBody);
      await sharedCommitSentMessage({
        messages: webMessagesPersister,
        conversations: webConversationsPersister,
        id: localId,
        conversationId,
        contentHash: contentHashHex,
        epoch,
        serverSeq: sentAt,
        sentAt,
        senderAccountId: identity.accountId,
        ...(preview !== undefined ? { preview } : {}),
      });
    }
    log.debug({
      event: 'video.send.ok',
      conversationIdPrefix: conversationId.slice(0, 6),
      mediaIdPrefix: mediaId.slice(0, 8),
      sizeBytes: file.size,
    });
    return {
      contentHashHex,
      messageId: localId,
      videoObjectUrl: probed.objectUrl,
      ...(probed.posterObjectUrl !== undefined ? { posterObjectUrl: probed.posterObjectUrl } : {}),
    };
  }

  const messageId = eventId;

  if (identity) {
    await sharedPersistSentMessage({
      messages: webMessagesPersister,
      conversations: webConversationsPersister,
      record: {
        id: messageId,
        conversationId,
        epoch,
        serverSeq: sentAt,
        contentHash: contentHashHex,
        senderAccountId: identity.accountId,
        body: payloadBody,
        sentAt,
        deliveryState: 'sent',
        ...(parentId !== undefined
          ? { inReplyTo: { messageId: parentId, preview: 'Video' } }
          : {}),
      },
    });
  } else {
    log.warn({ event: 'video.persistSent.no_identity' });
  }

  return {
    contentHashHex,
    messageId,
    videoObjectUrl: probed.objectUrl,
    ...(probed.posterObjectUrl !== undefined ? { posterObjectUrl: probed.posterObjectUrl } : {}),
  };
}

export function isVideoEnvelopeBody(body: string | undefined): boolean {
  if (body === undefined || body.length === 0) return false;
  if (!body.startsWith('{')) return false;
  return body.includes('"com.ping.video"');
}

export function parseVideoEnvelope(body: string | undefined): VideoEnvelopePayload | undefined {
  if (body === undefined) return undefined;
  try {
    const parsed = JSON.parse(body) as Partial<VideoEnvelope>;
    if (parsed.kind !== 'com.ping.video') return undefined;
    const p = parsed.payload;
    const ref = p?.blob_ref;
    if (
      p === undefined ||
      ref === undefined ||
      typeof ref.media_id !== 'string' ||
      typeof ref.content_key !== 'string' ||
      typeof ref.content_iv !== 'string' ||
      typeof ref.hash !== 'string'
    ) {
      return undefined;
    }
    return {
      blob_ref: ref,
      ...(p.poster_ref !== undefined ? { poster_ref: p.poster_ref } : {}),
      w: typeof p.w === 'number' ? p.w : 0,
      h: typeof p.h === 'number' ? p.h : 0,
      dur_ms: typeof p.dur_ms === 'number' ? p.dur_ms : 0,
      mime: typeof p.mime === 'string' ? p.mime : 'video/mp4',
      ...(typeof p.caption === 'string' ? { caption: p.caption } : {}),
    };
  } catch {
    return undefined;
  }
}

function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i++) {
    out += (b[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}
